import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageUploader({ value = [], onChange, multiple = true, label = "Images", maxItems = null, testIdPrefix = "" }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const list = Array.isArray(value) ? value : (value ? [value] : []);
  const tid = (suffix) => testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix;

  const handleFiles = async (files) => {
    if (!files?.length) return;
    let incoming = Array.from(files);
    if (maxItems != null) {
      const room = Math.max(0, maxItems - list.length);
      if (room === 0) {
        toast.error(`You can upload up to ${maxItems} image${maxItems === 1 ? "" : "s"}.`);
        return;
      }
      if (incoming.length > room) {
        toast.message(`Only the first ${room} image${room === 1 ? "" : "s"} were added (${maxItems} max).`);
        incoming = incoming.slice(0, room);
      }
    }
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of incoming) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BACKEND_URL}${data.url}`);
      }
      onChange(multiple ? [...list, ...uploaded] : uploaded[0]);
      toast.success(`${uploaded.length} image(s) uploaded`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(multiple ? next : (next[0] || ""));
  };

  return (
    <div data-testid={tid("image-uploader")}>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {list.map((src, i) => (
          <div key={i} className="relative w-20 h-20 border border-[var(--border)] overflow-hidden group">
            <img src={src} alt="" className="w-full h-full object-cover"/>
            <button type="button" onClick={()=>remove(i)}
                    className="absolute top-1 right-1 bg-[var(--surface-2)]/90 p-0.5 opacity-0 group-hover:opacity-100"
                    data-testid={tid(`remove-image-${i}`)}>
              <X className="w-3 h-3"/>
            </button>
          </div>
        ))}
      </div>
      <label
        onDragOver={(e)=>e.preventDefault()}
        onDrop={(e)=>{e.preventDefault(); handleFiles(Array.from(e.dataTransfer.files));}}
        className="block border-2 border-dashed border-[var(--border)] hover:border-[var(--primary-text)] p-4 cursor-pointer transition-colors text-center text-sm"
        style={{color:"var(--text-2)"}}>
        <input ref={fileRef} type="file" accept="image/*" multiple={multiple}
               onChange={(e)=>handleFiles(Array.from(e.target.files))}
               className="hidden" data-testid={tid("file-input")}/>
        {busy ? <Loader2 className="w-4 h-4 inline animate-spin mr-2"/> : <Upload className="w-4 h-4 inline mr-2"/>}
        {busy ? "Uploading…" : (maxItems != null
          ? `Drop image(s) or click to upload (${list.length}/${maxItems})`
          : "Drop image(s) or click to upload")}
      </label>
    </div>
  );
}
